import React, { useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../theme";
import SegmentedControl from "./SegmentedControl";

interface LegendItem {
	label: string;
	color: string;
}

interface ChartCardProps {
	title: string;
	note?: string;
	legend?: LegendItem[];
	renderChart: () => React.ReactNode;
	renderTable: () => React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, note, legend, renderChart, renderTable }) => {
	const { t } = useTranslation();
	const [view, setView] = useState<"chart" | "table">("chart");

	return (
		<Paper
			elevation={0}
			sx={{
				borderRadius: 3,
				border: `1px solid ${zoullooColors.border.light}`,
				p: 2.5,
				height: "100%",
				display: "flex",
				flexDirection: "column"
			}}
		>
			<Box display="flex" alignItems="flex-start" gap={1.5} mb={1.5}>
				<Box flex={1} minWidth={0}>
					<Typography variant="h6" fontWeight={700}>
						{title}
					</Typography>
					{note && (
						<Typography variant="caption" color="text.secondary">
							{note}
						</Typography>
					)}
				</Box>
				<SegmentedControl
					ariaLabel={title}
					options={[
						{ value: "chart", label: t("communityDashboard.view.chart") },
						{ value: "table", label: t("communityDashboard.view.table") }
					]}
					value={view}
					onChange={setView}
				/>
			</Box>
			{legend && (
				<Box display="flex" gap={2} flexWrap="wrap" mb={1.5}>
					{legend.map(item => (
						<Box key={item.label} display="flex" alignItems="center" gap={0.75}>
							<Box
								sx={{
									width: 11,
									height: 11,
									borderRadius: "3px",
									backgroundColor: item.color
								}}
							/>
							<Typography variant="caption" color="text.secondary">
								{item.label}
							</Typography>
						</Box>
					))}
				</Box>
			)}
			<Box flex={1}>{view === "chart" ? renderChart() : renderTable()}</Box>
		</Paper>
	);
};

export default ChartCard;
